import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';

import { fetchBrands, fetchBrandVenues, fetchVenues, MERCHANT_ENTERPRISE_SERVICE } from 'apis/enterprise/api';
import { Brand, Enterprise } from 'apis/enterprise/types';
import { Merchant } from 'apis/restaurant-api/merchant-admin/merchant/types';
import { fetchMerchantVenuesV3 } from 'apis/restaurant-api/merchant-admin/venue/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';

type Entity =
  | {
      type: 'venue';
      venue: Venue;
    }
  | { type: 'merchant'; merchant: Merchant }
  | { type: 'brand'; brand: Brand }
  | { type: 'enterprise'; enterprise: Enterprise };

// We only need to deduce the timezone from one venue
const VENUE_PAGE_SIZE = 1;

/**
 * A react hook to return the resolved timezone for a venue, merchant, brand or enterprise.
 * For merchant, brand and enterprise, it picks its first venue's timezone as the resolved timezone.
 * @param {Entity} entity
 * @returns {string} timezone
 */
const useTimezone = (entity: Entity): string => {
  const defaultUserTimezone = DateTime.local().zoneName;

  const isEnableFetchMerchantVenues = entity.type === 'merchant';
  const merchantId = isEnableFetchMerchantVenues ? entity.merchant.itemid.$oid : '';
  const { data: merchantVenueTimezone } = useQuery(
    [`/merchants/${merchantId}/timezone`],
    () => fetchMerchantVenueTimezone(merchantId),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: isEnableFetchMerchantVenues && Boolean(merchantId),
    },
  );

  const isEnableFetchBrandVenues = entity.type === 'brand';
  const brandId = isEnableFetchBrandVenues ? entity.brand.id : '';
  const { data: brandVenueTimezone } = useQuery(
    [`${MERCHANT_ENTERPRISE_SERVICE}/brands/${brandId}/timezone`],
    () => fetchBrandVenueTimezone(brandId),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: isEnableFetchBrandVenues && Boolean(brandId),
    },
  );

  const isEnableFetchEnterpriseVenues = entity.type === 'enterprise';
  const enterpriseId = isEnableFetchEnterpriseVenues ? entity.enterprise.id : '';
  const { data: enterpriseVenueTimezone } = useQuery(
    [`${MERCHANT_ENTERPRISE_SERVICE}/enterprises/${enterpriseId}/timezone`],
    () => fetchEnterpriseVenueTimezone(enterpriseId),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: isEnableFetchEnterpriseVenues && Boolean(enterpriseId),
    },
  );

  switch (entity.type) {
    case 'venue':
      return entity.venue.timezone;
    case 'merchant':
      return merchantVenueTimezone || defaultUserTimezone;
    case 'brand':
      return brandVenueTimezone || defaultUserTimezone;
    case 'enterprise':
      return enterpriseVenueTimezone || defaultUserTimezone;
    default:
      throw new Error('useTimezone only takes `venue`, `merchant`, `brand` or `enterprise` entity type!');
  }
};

const fetchMerchantVenueTimezone = async (merchantId: string) => {
  const { venues } = await fetchMerchantVenuesV3(merchantId, VENUE_PAGE_SIZE, '');
  if (venues.length === 0) {
    return;
  }
  const [{ timezone }] = venues;

  return timezone;
};

const fetchBrandVenueTimezone = async (brandId: string) => {
  const brandVenueIds = await fetchBrandVenues(brandId);
  if (brandVenueIds.length === 0) {
    return;
  }
  const [{ venueId }] = brandVenueIds;

  const { results: brandVenues } = await fetchVenues([venueId]);
  if (brandVenues.length > 0) {
    const [{ timezone }] = brandVenues;
    return timezone;
  }
  return;
};

const fetchEnterpriseVenueTimezone = async (enterpriseId: string) => {
  const brands = await fetchBrands(enterpriseId);
  for (const brandItem of brands) {
    const brandVenues = await fetchBrandVenues(brandItem.brand.id);
    if (brandVenues.length) {
      const [{ venueId }] = brandVenues;
      const { results: enterpriseVenues } = await fetchVenues([venueId]);
      if (enterpriseVenues.length > 0) {
        const [{ timezone }] = enterpriseVenues;
        return timezone;
      }
    }
  }
  return;
};

export default useTimezone;
